import * as Yup from "yup";
import { toGregorian, toEthiopian } from "ethiopian-date";

const today = new Date();
const minDate = new Date(
  today.getFullYear() - 110,
  today.getMonth(),
  today.getDate()
);
const maxDate = new Date(
  today.getFullYear() - 5,
  today.getMonth(),
  today.getDate()
);

const validateGregorianDate = (str) => {
  if (!str) return false;
  const [y, m, d] = str.split("/").map(Number);
  if (!y || !m || !d) return false;
  if (m < 1 || m > 12) return false;
  const daysInMonth = [31,28,31,30,31,30,31,31,30,31,30,31];
  if (m === 2) {
    const isLeap = (y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0));
    if (d < 1 || d > (isLeap ? 29 : 28)) return false;
  } else {
    if (d < 1 || d > daysInMonth[m - 1]) return false;
  }
  return true;
};

const validateEthiopianDate = (str) => {
  if (!str) return false;
  const [y, m, d] = str.split("/").map(Number);
  if (!y || !m || !d) return false;
  if (m < 1 || m > 13) return false;
  if (m <= 12) {
    if (d < 1 || d > 30) return false;
  } else {
    const isLeap = ((y + 1) % 4) === 0;
    if (d < 1 || d > (5 + (isLeap ? 1 : 0))) return false;
  }
  return true;
};

export const formSchema = Yup.object().shape({
  fullnameEng: Yup.string()
    .matches(
      /^([A-Z][a-z]+)\s((?:[A-Z][a-z]+|[A-Z]\.|[A-Z]|\.))\s((?:[A-Z][a-z]+|[A-Z]\.|[A-Z]|\.))(\s(?:[A-Z][a-z]+|[A-Z]\.|[A-Z]|\.))?$/,
      "Please enter a valid full name with each word starting with a capital letter. You can use spaces and periods."
    )
    .min(3, "Full name Too Short!")
    .required("Full name Required"),
  fullnameAmh: Yup.string()
    .matches(
      /^([\u1200-\u137F]+\.?|\.)(\s([\u1200-\u137F]+\.?|\.)){2,3}$/,
      "Full name in Amharic must contain only Amharic characters, spaces, and periods"
    )
    .min(3, "Full name in Amharic Too Short!")
    .required("Full name in Amharic Required"),
  email: Yup.string().email("Invalid email"),
  nationality: Yup.string().required("Nationality is required"),
  phone: Yup.string()
    .test(
      "valid-phone",
      "The phone number must either be 10 digits starting with 09 or 07, or 12 digits starting with 251.",
      (value) => {
        if (!value) return false;

        if (
          (value.startsWith("09") || value.startsWith("07")) &&
          /^\d{10}$/.test(value)
        ) {
          return true;
        }

        if (value.startsWith("+251") && /^\+251\d{9}$/.test(value)) {
          return true;
        }

        return false;
      }
    )
    .required("Phone number is required"),

  dateOfBirth: Yup.string()
    .required("Date of Birth is required")
    .test(
      "valid-dob",
      "Date of Birth must be in yyyy/mm/dd format and age must be above 5 years old",
      function (value) {
        if (!value) return false;

        const [year, month, day] = value.split("/").map(Number);
        if (!day || !month || !year) return false;

        try {
          const gDate = new Date(year, month - 1, day);
          return gDate >= minDate && gDate <= maxDate;
        } catch (err) {
          return false;
        }
      }
    ),
  dobAmh: Yup.string()
    .required("ትውልድ ቀን ይሙሉ")
    .test(
      "valid-dob-amh",
      "የትውልድ ቀን በ yyyy/mm/dd ቅርጸ ተከታታይ እና ከ 5 ዓመት በላይ መሆን አለበት",
      function (value) {
        if (!value) return false;

        const [year, month, day] = value.split("/").map(Number);
        if (!year || !month || !day) return false;

        try {
          const gregorian = toGregorian(year, month, day);
          if (!gregorian) return false;

          const gDate = new Date(gregorian[0], gregorian[1] - 1, gregorian[2]);

          if (gDate < minDate || gDate > maxDate) return false;

          const dateOfBirth = this.parent.dateOfBirth;
          if (dateOfBirth) {
            const [gYear, gMonth, gDay] = dateOfBirth.split("/").map(Number);
            const fullGYear =
              gYear < 100 ? (gYear <= 20 ? 2000 + gYear : 1900 + gYear) : gYear;
            const gregFromField = new Date(fullGYear, gMonth - 1, gDay);

            if (gregFromField.getTime() !== gDate.getTime()) return false;
          }

          return true;
        } catch (err) {
          return false;
        }
      }
    ),

  // woreda: Yup.string()
  //   .notOneOf(["0"], "Woreda cannot be empty")
  //   .required("Woreda is required"),
  // zone: Yup.string()
  //   .notOneOf(["0"], "Zone cannot be empty")
  //   .required("Zone is required"),
  gender: Yup.mixed()
    .oneOf(["Male", "Female"], "Please select a gender")
    .required("Gender is required"),
});

export const updateFormSchema = (initialState) =>
  Yup.object().shape({
    fullnameEng: Yup.string()
      .matches(
        /^([A-Z][a-z]+)\s((?:[A-Z][a-z]+|[A-Z]\.|[A-Z]|\.))\s((?:[A-Z][a-z]+|[A-Z]\.|[A-Z]|\.))(\s(?:[A-Z][a-z]+|[A-Z]\.|[A-Z]|\.))?$/,
        "Please enter a valid full name with each name starting with a capital letter. You can use spaces and periods."
      )
      .min(3, "Full name Too Short!")
      .required("Full name Required"),

    fullnameAmh: Yup.string()
      .matches(
        /^([\u1200-\u137F]+\.?|\.)(\s([\u1200-\u137F]+\.?|\.)){2,3}$/,
        "Full name in Amharic must contain only Amharic characters, spaces, and periods"
      )
      .min(3, "Full name in Amharic Too Short!")
      .required("Full name in Amharic Required"),
    email: Yup.string().email("Invalid email"),
    nationality: Yup.string().required("Nationality is required"),
    phone: Yup.string()
      .test(
        "valid-phone",
        "Phone number must be either 10 digits starting with 0 or 13 digits starting with +251",
        (value) => {
          if (!value) return false;

          if (value.startsWith("0") && /^\d{10}$/.test(value)) {
            return true;
          }

          if (value.startsWith("+251") && /^\+251\d{9}$/.test(value)) {
            return true;
          }

          return false;
        }
      )
      .required("Phone number is required"),
    dateOfBirth: Yup.string()
      .required("Date of Birth is required")
      .test(
        "valid-gregorian",
        "Date of Birth must be in YYYY/MM/DD and a real calendar date",
        (val) => validateGregorianDate(val)
      ),

    dobAmh: Yup.string()
      .required("ትውልድ ቀን ይሙሉ")
      .test(
        "valid-ethiopian",
        "ትውልድ ቀን በYYYY/MM/DD ቅርጽ እና ትክክለኛ የኢትዮጵያ ቀን መሆን ይገባል",
        (val) => validateEthiopianDate(val)
      ),
    // woreda: Yup.string()
    //   .notOneOf(["0"], "Woreda cannot be empty")
    //   .required("Woreda is required"),
    // zone: Yup.string()
    //   .notOneOf(["0"], "Zone cannot be empty")
    //   .required("Zone is required"),
    gender: Yup.mixed()
      .oneOf(["Male", "Female"], "Please select a gender")
      .required("Gender is required"),
    uploadPoaDocumentType: Yup.mixed().test(
      "is-required",
      "Select the document type you want to upload",
      function (value) {
        const { region, woreda, zone } = this.parent;
        const initialRegion = initialState?.region;
        const initialWoreda = initialState?.woreda;
        const initialZone = initialState?.zone;

        const hasChanged =
          region?.toString() !== initialRegion?.toString() ||
          woreda?.toString() !== initialWoreda?.toString() ||
          zone?.toString() !== initialZone?.toString();

        if (hasChanged) {
          return value !== undefined && value !== null && value !== "";
        }

        return true;
      }
    ),
    uploadPoiDocumentType: Yup.mixed().test(
      "is-required",
      "Select the document type you want to upload",
      function (value) {
        const { fullnameEng, fullnameAmh, dateOfBirth, dobAmh, nationality } = this.parent;

        const initialFullnameEng = initialState?.fullnameEng;
        const initialFullnameAmh = initialState?.fullnameAmh;
        const initialDateOfBirth = initialState?.dateOfBirth;
        const initialDobAmh = initialState?.dobAmh;
        const initialNationality = initialState?.nationality;

        const hasChanged =
          fullnameEng?.toString() !== initialFullnameEng?.toString() ||
          fullnameAmh?.toString() !== initialFullnameAmh?.toString() ||
          dateOfBirth?.toString() !== initialDateOfBirth?.toString() ||
          dobAmh?.toString() !== initialDobAmh?.toString() ||
          nationality?.toString() !== initialNationality?.toString();

        if (hasChanged) {
          return value !== undefined && value !== null && value !== "";
        }

        return true;
      }
    ),
    poaUploadProof: Yup.mixed()
      .test("file-size", "File size exceeds 5MB limit.", function (value) {
        if (value) {
          const maxSizeInBytes = 5 * 1024 * 1024;
          return value.size <= maxSizeInBytes;
        }
        return true;
      })
      .test(
        "is-required",
        "Please upload a supporting document when changing your region, woreda, or zone.",
        function (value) {
          const { region, woreda, zone } = this.parent;
          const initialRegion = initialState?.region;
          const initialWoreda = initialState?.woreda;
          const initialZone = initialState?.zone;

          const hasChanged =
            region?.toString() !== initialRegion?.toString() ||
            woreda?.toString() !== initialWoreda?.toString() ||
            zone?.toString() !== initialZone?.toString();

          if (hasChanged) {
            return value !== undefined && value !== null && value !== "";
          }

          return true;
        }
      )
      .test(
        "file-type",
        "File must be a PDF or an image (JPEG, PNG, GIF).",
        function (value) {
          if (value) {
            const fileType = value.type;
            const allowedTypes = [
              "application/pdf",
              "image/jpeg",
              "image/png",
              "image/jpg",
            ];
            return allowedTypes.includes(fileType);
          }
          return true;
        }
      ),
    poiUploadProof: Yup.mixed()
      .test("file-size", "File size exceeds 5MB limit.", function (value) {
        if (value) {
          const maxSizeInBytes = 5 * 1024 * 1024;
          return value.size <= maxSizeInBytes;
        }
        return true;
      })
      .test(
        "is-required",
        "Please upload a supporting document when changing your name, nationality, or date of birth.",
        function (value) {
          
          const { fullnameEng, fullnameAmh, dateOfBirth, nationality } = this.parent;
          const initialfullnameEng = initialState?.fullnameEng;
          const initialfullnameAmh = initialState?.fullnameAmh;
          const initialdateOfBirth = initialState?.dateOfBirth;
          const initialNationality = initialState?.nationality;

          const hasChanged =
            fullnameEng?.toString() !== initialfullnameEng?.toString() ||
            fullnameAmh?.toString() !== initialfullnameAmh?.toString() ||
            dateOfBirth?.toString() !== initialdateOfBirth?.toString() ||
            nationality?.toString() !== initialNationality?.toString();
            
          if (hasChanged) {
            return value !== undefined && value !== null && value !== "";
          }

          return true;
        }
      )
      .test(
        "file-type",
        "File must be a PDF or an image (JPEG, PNG, GIF).",
        function (value) {
          if (value) {
            const fileType = value.type;
            const allowedTypes = [
              "application/pdf",
              "image/jpeg",
              "image/png",
              "image/jpg",
            ];
            return allowedTypes.includes(fileType);
          }
          return true;
        }
      ),
  });
